import dayjs, { Dayjs } from "dayjs";
import {
  Line as LineItem,
  OrderProcessRequest,
  SubLineItem,
} from "../../../api/orders/orderInterfaces";
import { Address, Line, Order, OrderType, SubLine } from "../types";

export const copyOrder = (order: Order): Order => ({ ...order });

const mapSubLineToProcessPayload = (subLine: SubLine): SubLineItem => {
  const currentValue: Map<string, string> | undefined =
    subLine.newServiceLevelAgreement?.currentValue;
  const slrCode = currentValue?.keys().next().value;
  return {
    scheduleLineNumber: subLine.lineNumber,
    confirmedPartNumber: subLine.partNumber,
    confirmedPartDescription: subLine.partDescription,
    confirmedQuantity: subLine.quantity,
    shippingUnit: subLine.shippingUnit,
    shippingUnitText: subLine.shippingUnitText,
    vendorDirectPart: subLine.vendorDirectPart,
    lineStatus: subLine.lineStatus,
    serviceLevelAgreement:
      subLine.serviceLevelAgreement?.currentValue === ""
        ? null
        : subLine.serviceLevelAgreement?.currentValue,
    serviceLevelAgreementCode: slrCode !== undefined ? slrCode : null,
    serviceLevelAgreementDescription:
      currentValue !== undefined && slrCode !== undefined
        ? currentValue?.get(slrCode)
        : null,
    carrier: subLine.carrier?.carrierValue,
    effectivePrice: subLine.effectivePrice,
    promotionId: subLine.promotion?.id as string,
    promiseDate: subLine.promiseDate?.toISOString(),
    deliveryNumber: subLine.deliveryNumber,
    isScheduleLineEligibleForM2NO: subLine.isScheduleLineEligibleForM2NO,
    isSubstituted: subLine.isSubstituted,
    shipDateTime: subLine.shipDateTime,
  };
};

const mapLineToLineProcessPayload = (
  line: Line,
  orderType: string,
  enableDynamicFeatureToggleForPromotion: boolean
): LineItem => {
  const currentValue: Map<string, string> | undefined =
    line.newServiceLevelAgreement?.currentValue;
  const slrCode = currentValue?.keys().next().value;
  return {
    lineNumber: line.lineNumber,
    quantity: line.quantity,
    partNumber: line.partNumber,
    lineStatus: line?.lineStatus as string,
    serviceLevelAgreement:
      line.serviceLevelAgreement?.currentValue === ""
        ? null
        : line.serviceLevelAgreement?.currentValue,
    serviceLevelAgreementCode: slrCode !== undefined ? slrCode : null,
    serviceLevelAgreementDescription:
      currentValue !== undefined && slrCode !== undefined
        ? currentValue?.get(slrCode)
        : null,
    bin: line.bin,
    isLineEligibleForM2NO: line.isLineEligibleForM2NO,
    promotionId:
      enableDynamicFeatureToggleForPromotion &&
      orderType === OrderType.PROMOTION_ORDER
        ? line.promotion?.id
        : undefined,
    scheduledLineItems: line.subLines?.map(mapSubLineToProcessPayload),
    uniqueLineId: line.uniqueLineId,
    engineSerialNumber: line.engineSerialNumber,
  };
};

export const getShipDate = (
  isFutureShipMonthPromotion: boolean,
  requestedShipMonth?: Dayjs
) => {
  if (!requestedShipMonth) {
    return undefined;
  }
  if (isFutureShipMonthPromotion) {
    return new Date(
      requestedShipMonth
        .endOf("month")
        .set("hour", 12)
        .set("minute", 0)
        .set("second", 0)
        .valueOf()
    ).toISOString();
  }

  return new Date(new Date().setHours(12, 0, 0, 0)).toISOString();
};

export const mapOrderToProcessPayloadWithoutLines = (
  order: Order
): Omit<OrderProcessRequest, "lineItems"> => ({
  accountId: order.accountId,
  countryCode: order.countryCode,
  orderNumber: order.orderNumber,
  orderType: order.orderType,
  orderServiceType: order.orderServiceType,
  orderConsolidation: order.orderConsolidation,
  dealerReference: order.dealerReference,
  orderNotes: order.orderNotes,
  shipToAddress: {
    name: order.shipToAddress?.name,
    addressLineOne: order.shipToAddress?.addressLine1,
    addressLineTwo: order.shipToAddress?.addressLine2,
    stateOrProvinceName: order.shipToAddress?.state,
    phoneNumber: order.shipToAddress?.phoneNumber,
    zipCode: order.shipToAddress?.postalCode,
    cityName: order.shipToAddress?.city,
    countryCode: order.shipToAddress?.country,
    shipMode: order.shipToAddress?.shipMode,
  },
  soldToAddress: {
    name: order.soldToAddress?.name,
    addressLineOne: order.soldToAddress?.addressLine1,
    addressLineTwo: order.soldToAddress?.addressLine2,
    stateOrProvinceName: order.soldToAddress?.state,
    phoneNumber: order.soldToAddress?.phoneNumber,
    zipCode: order.soldToAddress?.postalCode,
    cityName: order.soldToAddress?.city,
    countryCode: order.soldToAddress?.country,
  },
  isShipToAddressOverridden: order.isShipToAddressOverridden,
  outboundAutoSubmit: order.outboundAutoSubmit,
  shipDate: getShipDate(
    !(
      order.promotion?.shipFromDate === null &&
      order.promotion?.shipToDate === null
    ),
    order.requestedShipMonth
  ),
  discount: order.discount,
  discountDescription: order.discountDescription,
  ...(order.orderType === OrderType.PROMOTION_ORDER &&
    order.promotion?.id && { promotionId: order.promotion.id }),
});

export const createOrderProcessPayloadFromOrder = (
  order: Order,
  dynamicPromotionToggle: boolean
): OrderProcessRequest => ({
  ...mapOrderToProcessPayloadWithoutLines(order),
  lineItems: order.lines.map((line) =>
    mapLineToLineProcessPayload(line, order.orderType, dynamicPromotionToggle)
  ),
});

const createChunksOfLines = (lines: Line[], size: number): Line[][] => {
  const chunks: Line[][] = [];
  for (let i = 0; i < lines.length; i += size) {
    chunks.push(lines.slice(i, i + size));
  }
  return chunks;
};

export const createChunkedOrderProcessPayloadFromOrder = (
  order: Order,
  sizeOfChunk: number,
  dynamicPromotionToggle: boolean
): OrderProcessRequest[] => {
  const chunks = createChunksOfLines(order.lines, sizeOfChunk);
  return chunks.map((chunk) => ({
    ...mapOrderToProcessPayloadWithoutLines(order),
    lineItems: chunk.map((line) =>
      mapLineToLineProcessPayload(line, order.orderType, dynamicPromotionToggle)
    ),
  }));
};

const toTitleCase = (str: string): string =>
  str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );

export const convertAddressToTitleCase = (
  address: Address | undefined
): Address | undefined => ({
  ...address,
  name: toTitleCase(address?.name ?? ""),
  addressLine1: toTitleCase(address?.addressLine1 ?? ""),
  addressLine2: toTitleCase(address?.addressLine2 ?? ""),
  state: address?.state?.toUpperCase() ?? "",
  phoneNumber: address?.phoneNumber ?? "",
  postalCode: address?.postalCode ?? "",
  city: toTitleCase(address?.city ?? ""),
  country: address?.country?.toUpperCase() ?? "",
});

export function getValidRequestedShipMonth(
  requestedShipMonth: Dayjs | undefined,
  shipFromDate: string | null | undefined,
  shipToDate: string | null | undefined
): Dayjs | undefined {
  if (shipFromDate === null && shipToDate === null) {
    return requestedShipMonth &&
      requestedShipMonth.startOf("month").isSame(dayjs().startOf("month"))
      ? requestedShipMonth
      : undefined;
  }

  if (!requestedShipMonth || !shipFromDate || !shipToDate) return undefined;

  const from = dayjs(shipFromDate).startOf("month");
  const to = dayjs(shipToDate).endOf("month");
  const req = requestedShipMonth.startOf("month");

  if (
    req.isSame(from) ||
    req.isSame(to) ||
    (req.isAfter(from) && req.isBefore(to))
  ) {
    return requestedShipMonth;
  }

  return undefined;
}

const isEmpty = (value: string | undefined | null) =>
  value === undefined || value === null || value.trim() === "";

export const isStringSame = (
  oldValue: string | undefined | null,
  newValue: string | undefined | null
) => {
  if (isEmpty(oldValue) && isEmpty(newValue)) {
    return true;
  }

  return oldValue?.trim()?.toUpperCase() === newValue?.trim()?.toUpperCase();
};
